/**
 * Collapsed summary row for country threshold overrides.
 */

import type { FC } from 'react';
import { ChevronRight, Globe2, Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import { COUNTRY_THRESHOLD_HELP, CountBadge } from './country-threshold-parts';

interface CountryThresholdCollapsedProps {
  rowCount: number;
  isLoading: boolean;
  disableAdd: boolean;
  className?: string;
  onExpand: () => void;
  onAdd: () => void;
}

export const CountryThresholdCollapsed: FC<CountryThresholdCollapsedProps> = ({
  rowCount,
  isLoading,
  disableAdd,
  className,
  onExpand,
  onAdd,
}) => (
  <div className={cn('border-t border-border/60 bg-muted/15', className)}>
    <div className="flex items-center gap-1.5 px-3 py-1.5">
      <button
        type="button"
        onClick={onExpand}
        className="group flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 pl-1 pr-2 text-left text-xs transition-colors hover:bg-background/70"
        title={COUNTRY_THRESHOLD_HELP}
      >
        <Globe2
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground"
          aria-hidden
        />
        <span className="font-medium text-foreground">Overrides</span>
        <CountBadge n={rowCount} />
        <ChevronRight
          className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-70 group-hover:opacity-100"
          aria-hidden
        />
      </button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 shrink-0 gap-1 px-2 text-xs"
        onClick={onAdd}
        disabled={isLoading || disableAdd}
        title="Add country override row"
      >
        <Plus className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Add</span>
      </Button>
    </div>
  </div>
);
